import ProductAddToCartHOC from '@common/components/ProductAddToCartHOC';
import ProductCards from '@common/components/ProductCards';
import Product, { computeProductLink, PRODUCT_REFERRED_FROM } from '@common/models/Product';
import { RootState } from '@common/store';
import { addProductToCart, subtractProductFromCart } from '@common/store/cart/slice';
import { FeaturesSliceType } from '@common/store/features/slice';
import {
  fetchProductDetailsRequest,
  fetchProductReviewsRequest,
  ProductDetailsSliceType,
} from '@common/store/productDetails/slice';
import { StoreInfoSliceType } from '@common/store/storeInfo/slice';
import { addToast, removeToast, ToastWithKeyType } from '@common/store/toasts/slice';
import { getMerchantJWTToken } from '@common/utils/token';
import Event from '@components/Event';
import QuickViewProduct from '@components/Product';
import ProductCardOverlay from '@components/ProductCardOverlay';
import Toasts from '@components/Toasts';
import Image from '@lib/Image';
import Close from '@Sakra/images/Close.svg';
import classnames from 'classnames';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { EmptyProductCard } from '../../../../common/components/EmptyProductCard';
import Modal from './Modal';

export interface ProductCardProps {
  product: Product;
  storeInfo: StoreInfoSliceType;
  cartProductQuantity: number;
  productDetails: ProductDetailsSliceType;
  addProductToCart: typeof addProductToCart;
  subtractProductFromCart: typeof subtractProductFromCart;
  fetchProductDetailsRequest: typeof fetchProductDetailsRequest;
  fetchProductReviewsRequest: typeof fetchProductReviewsRequest;
  toasts: ToastWithKeyType[];
  removeToast: typeof removeToast;
  hideQuickView?: boolean;
  referredFrom?: PRODUCT_REFERRED_FROM;
  shopAll?: string;
  features: FeaturesSliceType;
}

const ProductCard: React.FunctionComponent<ProductCardProps> = ({
  product,
  addProductToCart,
  subtractProductFromCart,
  cartProductQuantity,
  productDetails,
  toasts,
  removeToast,
  fetchProductDetailsRequest,
  fetchProductReviewsRequest,
  storeInfo,
  hideQuickView,
  referredFrom,
  shopAll,
  features,
}) => {
  const [CloseDetails, SetCloseDetails] = useState(false);
  let dimensionH = '';
  let dimensionW = '';
  if (shopAll === 'search' || shopAll === 'category') {
    dimensionH = 'h-155 md:h-286';
    dimensionW = 'w-155 md:w-286';
  } else if (shopAll === 'shopAll') {
    dimensionH = 'h-155 md:h-280';
    dimensionW = 'w-155 md:w-280';
  } else {
    dimensionH = 'h-184 md:h-286';
    dimensionW = 'w-184 md:w-286';
  }

  if (!product) {
    return <EmptyProductCard dimensionW={dimensionW} />;
  }

  const computedProductLink = computeProductLink({ product, referredFrom });

  return (
    <div className={`${dimensionW} rounded`}>
      {CloseDetails && productDetails.productDetails ? (
        <Modal
          heading=""
          closeModal={() => {
            document.body.style.overflow = 'scroll';
            SetCloseDetails(false);
          }}
          className={classnames('zoomIn modal-open QuickView md:w-fit z-999 md:mx-0 mb-100 rounded', {
            'mt-150 md:mt-0': !getMerchantJWTToken(),
            'mt-220 md:mt-0': getMerchantJWTToken(),
          })}
        >
          <div className="relative">
            {toasts.length ? (
              <div className=" w-full md:w-547 md:absolute md:-mt-10 md:right-0 md:-mr-10" id="QuickViewToasts">
                <Toasts toasts={toasts} addToast={addToast} removeToast={removeToast} quickView={true} />
              </div>
            ) : null}
            <div className="flex justify-between mb-30 md:mb-16 items-center">
              <div
                className="text-title text-20 md:text-24 flex w-full justify-center md:justify-start font-medium"
                font-customisation="section-heading"
                color-customisation="page-heading"
              >
                Quick View
              </div>
              {!toasts.length && (
                <Image
                  src={Close}
                  alt="Close"
                  className="float-right cursor-pointer h-12 w-12 md:h-14 md:w-14"
                  onClick={() => {
                    document.body.style.overflow = 'scroll';
                    SetCloseDetails(!CloseDetails);
                  }}
                  noDefaultDimensions
                  noDefaultFit
                />
              )}
            </div>

            <ProductAddToCartHOC
              variantSetToMinQuantity={true}
              disableSuccessToasts={() => false}
              product={productDetails.productDetails}
              noInitialVariant
              noInitialQuantity
              render={(hocArgs) => {
                const { finalProduct } = hocArgs;
                return (
                  <div className="overflow-x-auto hideScroll">
                    {productDetails.productDetails.isEvent ? (
                      <Event
                        subtractProductFromCart={subtractProductFromCart}
                        product={productDetails.productDetails}
                        addProductToCart={addProductToCart}
                        addToast={addToast}
                        cartProductQuantity={cartProductQuantity}
                        isZoomEnabled={false}
                        storeType={storeInfo.storeInfo?.storeType}
                        quickView={true}
                        review={productDetails?.reviews?.length}
                        finalProduct={finalProduct}
                        hocArgs={hocArgs}
                        productVideoFeature={features.features?.productVideo}
                      />
                    ) : (
                      <QuickViewProduct
                        subtractProductFromCart={subtractProductFromCart}
                        product={productDetails.productDetails}
                        addProductToCart={addProductToCart}
                        addToast={addToast}
                        cartProductQuantity={cartProductQuantity}
                        isZoomEnabled={false}
                        storeType={storeInfo.storeInfo?.storeType}
                        review={productDetails.reviews?.length}
                        quickView={true}
                        finalProduct={finalProduct}
                        hocArgs={hocArgs}
                        productVideoFeature={features.features?.productVideo}
                      />
                    )}
                  </div>
                );
              }}
            />
            <div className="md:hidden pt-24"></div>
          </div>
        </Modal>
      ) : null}

      <ProductCards
        product={product}
        addProductToCart={addProductToCart}
        referredFrom={referredFrom}
        mainDivClassNames="text-left"
        imageDivClassNames={`zoomIn rounded-t-4 ${dimensionW}`}
        infoMainDivClassName="bg-card_bg px-10 py-20"
        discountPercentClassNames={'hidden'}
        titleClassName={
          'font-regular text-productCardTitleSm md:text-productCardTitleMd md:w-260 truncate text-productCard-title md:leading-24 leading-22 pb-10 h-36'
        }
        priceClassName={
          'font-bold leading-22 md:leading-24 text-productCardPriceSm md:text-productCardPriceMd mr-10 text-productCard-finalPrice'
        }
        discountPriceClassName={
          'leading-22 md:leading-24 text-productCardDiscountPriceSm md:text-productCardDiscountPriceMd text-grayBorder text-productCard-mrp'
        }
        includeTaxClassName="hidden"
        dimensionH={dimensionH}
        dimensionW={dimensionW}
        quickViewDiv={
          !hideQuickView && (
            <ProductCardOverlay
              product={product}
              fetchProductDetailsRequest={fetchProductDetailsRequest}
              fetchProductReviewsRequest={fetchProductReviewsRequest}
              removeToast={removeToast}
              toasts={toasts}
              CloseDetails={CloseDetails}
              SetCloseDetails={SetCloseDetails}
              computedProductLink={computedProductLink}
            />
          )
        }
      />
    </div>
  );
};

export default connect(
  ({ productDetails, toasts, features }: RootState) => ({
    productDetails,
    toasts,
    features,
  }),
  {
    fetchProductDetailsRequest,
    fetchProductReviewsRequest,
    removeToast,
  },
)(ProductCard);
